import { PropsWithChildren, useEffect } from "react"
import { useDispatch } from "react-redux"
import { Navigate, useLocation } from "react-router-dom"

import { setUser } from "@/store/user/userSlice"
import { useGetMeQuery } from "@/store/userAPI"

import routes from "./routes.ts"

const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { data, isLoading } = useGetMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: import.meta.env.DEV,
  })

  // Restore identity from the verified cookie session, including a new tab or
  // cleared local storage. A valid session must not leave the profile empty.
  useEffect(() => {
    if (data?.success && data.user) dispatch(setUser(data.user))
  }, [data, dispatch])

  if (import.meta.env.DEV) return children

  // Only blank the route during the initial session check. Nested protected
  // routes can trigger a background refetch; hiding the parent for that fetch
  // unmounts the child, which remounts and refetches in an endless loop.
  if (isLoading) {
    return null
  }

  if (!data?.success) {
    return <Navigate replace to={routes.login} />
  }

  // Older backend deployments authenticate correctly but do not yet return
  // session metadata. Allow the requested route during a rolling deploy; the
  // verify response already sends new users directly to company details.
  if (!data.session) {
    return children
  }

  const organizationReady = data.session.organizationSetup?.complete ?? Boolean(data.session.tenantId)
  const isOnCompanyDetails = location.pathname === routes.companyDetails

  // Base organisation readiness is independent of the Climate journey.
  if (organizationReady && isOnCompanyDetails) {
    return <Navigate replace to={routes.dashboard} />
  }

  // Resume incomplete base setup after login or direct navigation.
  if (!organizationReady && !isOnCompanyDetails) {
    return <Navigate replace to={routes.companyDetails} />
  }

  // Otherwise, render the requested component
  return children
}

export default ProtectedRoute
